import React, {useCallback, useEffect, useRef} from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {getMarkerTheme} from './markerTheme';
import CommentsSection from '../../../comments/CommentsSection';
import {useYCommentsCount} from '../../../comments/useYCommentsCount';

// Static sx objects hoisted to module scope
const HEADER_STACK_SX = {width: '100%', minHeight: 40, p: 1, cursor: 'pointer'};
const ICON_BOX_SX = {
    width: 26, height: 26, borderRadius: 999,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
};
const DELETE_BTN_SX = {width: 24, height: 24, padding: 0};
const COMMENT_CONTENT_SX = {width: '100%', overflow: 'hidden'};

const CommentMarker = React.memo(function CommentMarker({marker, isActive, created, onClick, onThreadSnapshot, onDelete}) {
    const {count, isLoaded} = useYCommentsCount({
        elementName: marker?.type ?? 'comment',
        elementId: marker?.uid,
    });

    const shouldAutoFocus = created === true;
    const cfg = getMarkerTheme('comment');
    const Icon = cfg.Icon;

    // Track latest count/isLoaded in refs so the blur handler can read them
    // without stale closures
    const countRef = useRef(count);
    const isLoadedRef = useRef(isLoaded);
    useEffect(() => {
        countRef.current = count;
        isLoadedRef.current = isLoaded;
    }, [count, isLoaded]);

    useEffect(() => {
        if (!marker?.uid) return;
        onThreadSnapshot?.({
            type: marker?.type ?? 'comment',
            uid: marker.uid,
            count,
            isLoaded,
        });
    }, [count, isLoaded, marker?.type, marker?.uid, onThreadSnapshot]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.(e);
        }
    }, [onClick]);

    const handleDeleteClick = useCallback((e) => {
        e.stopPropagation();
        onDelete?.(marker);
    }, [onDelete, marker]);

    // When focus leaves the entire marker card, remove it if the thread is
    // still empty (no comments have been submitted yet).
    const handleBlur = useCallback((e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        if (isLoadedRef.current && countRef.current === 0) {
            onDelete?.(marker);
        }
    }, [marker, onDelete]);

    return (
        <Box
            className="pm-margin-marker pm-margin-marker--expanded"
            onBlur={handleBlur}
            sx={{
                width: '100%',
                minHeight: 40,
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: isActive ? '1px solid rgba(66,133,244,0.55)' : '1px solid transparent',
                boxShadow: isActive ? '0 6px 18px rgba(0,0,0,0.18)' : '0 0px 0px rgba(0,0,0,0)',
                userSelect: 'none',
                transition: 'box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
                zIndex: isActive ? 999 : 'auto',
                ...(!isActive && {
                    '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.04)',
                    },
                }),
            }}
        >
            {/* Header - consistent in both states */}
            <Stack
                role="button"
                direction="row"
                alignItems="center"
                gap={1}
                tabIndex={0}
                onClick={onClick}
                onKeyDown={handleKeyDown}
                sx={HEADER_STACK_SX}
            >
                {Icon ? (
                    <Box sx={{...ICON_BOX_SX, bgcolor: cfg.bg, border: `1px solid ${cfg.border}`}}>
                        <Icon fontSize="small" sx={{color: cfg.color}}/>
                    </Box>
                ) : null}
                <Typography variant="subtitle2" sx={{fontWeight: 600, flex: 1}}>
                    {isLoaded && count > 0 ? `${count} Comment${count === 1 ? '' : 's'}` : 'Comments'}
                </Typography>
                {(isActive && onDelete) ? (
                    <IconButton
                        size="small"
                        onClick={handleDeleteClick}
                        sx={DELETE_BTN_SX}
                    >
                        <DeleteOutlineIcon fontSize="small"/>
                    </IconButton>
                ) : null}
            </Stack>

            {/* Expandable content */}
            {isActive ? (
                <Box
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    sx={COMMENT_CONTENT_SX}
                >
                    <CommentsSection
                        props={{
                            fullHeight: false,
                            elementName: marker?.type ?? 'comment',
                            elementId: marker?.uid,
                            autoFocus: shouldAutoFocus,
                        }}
                    />
                </Box>
            ) : null}
        </Box>
    );
});

export default CommentMarker;
